// IT 監控 › 遠端協助（`kmgr-remote` `/kmgr/remote`，SPEC #kinio-kms-ui 導覽表、#kinio-kms-remote；k1-wbs M4-06）：
//   協助請求（N）：員工在系統匣按〔請求遠端協助〕→「〈主要使用者或主機名稱〉請求協助 · 求助碼 482 915」〔開始協助〕（理由預帶求助碼與問題）；
//   紀錄：期間內的每次協助（開始、主機、操作者、時長、權限、結果、備註 / 外部工單）、〔匯出 CSV〕；自己進行中的〔回到協助〕。
// 權限：協助請求要 kmgr.remote.attended（主機清單的 can_remote 全部 false = 權限不足的畫面）；紀錄由 kmgr 依權限給
// （attended 或組織擁有者 / 管理員看整個家戶，其他人只看自己發起的，access 寫在畫面上；M4-06b）。
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useScope } from '../../../lib/scope';
import { stamp } from '../../../lib/timefmt';
import { SkeletonLines } from '../../../components/Skeleton';
import { useKmgrEnv } from '../hooks';
import { kmgrScopeOf } from '../model';
import { Crumbs, errText, LiveStamp, NS, StateBox } from '../parts';
import type { HostRow } from '../types';
import { loadRecords } from './api';
import { remoteAccess, useApiOf, useAssistRequests, useMemberNames, useScopeHosts } from './hooks';
import { defaultRange, durationSeconds, durationText, fmtCode, permsText, rangeIso, recordsCsv, resultText, type RecordRow } from './model';
import { RemoteDialog, type RemoteTarget } from './RemoteDialog';
import { isPhone, targetOf } from './target';
import type { AssistRequest, RemoteSession } from './types';

type Tab = 'requests' | 'records';

export function RemotePage() {
  const { t } = useTranslation(NS);
  const { me } = useKmgrEnv();
  const [sp] = useSearchParams();
  const tab: Tab = sp.get('tab') === 'records' ? 'records' : 'requests';
  const hostsQ = useScopeHosts();
  const hosts = hostsQ.data;
  const access = remoteAccess(hosts);
  const assists = useAssistRequests(hosts);
  const forbidden = tab === 'requests' && access.checked && access.allowed.size === 0 && access.denied.size > 0;
  const [open, setOpen] = useState<{
    target: RemoteTarget;
    assist?: AssistRequest;
    resume?: string;
  } | null>(null);
  const apiOf = useApiOf();
  const n = assists.list.length;
  return (
    <section className="page km" data-testid="km-remote-page">
      <Crumbs items={[{ label: t('nav.group'), to: '/kmgr/incidents' }, { label: t('nav.remote') }]} />
      <div className="km-head">
        <h1 className="page-title">{t('nav.remote')}</h1>
        <span className="spacer" />
        {tab === 'requests' && <LiveStamp updatedAt={assists.updatedAt} stale={assists.failed > 0} />}
      </div>
      <nav className="km-tabs" aria-label={t('remote.tabs')}>
        <Link to="/kmgr/remote" aria-current={tab === 'requests' ? 'page' : undefined}>
          {assists.loading ? t('remote.tab_requests') : t('remote.tab_requests_n', { n })}
        </Link>
        <Link to="/kmgr/remote?tab=records" aria-current={tab === 'records' ? 'page' : undefined}>
          {t('remote.tab_records')}
        </Link>
      </nav>
      {hostsQ.isLoading ? (
        <div className="card">
          <SkeletonLines n={4} label={t('common.loading')} />
        </div>
      ) : hostsQ.isError && !hosts ? (
        <StateBox icon="⊘" title={t('remote.unreadable')} body={errText(t, hostsQ.error)} testId="km-remote-unreadable">
          <button className="btn" onClick={() => void hostsQ.refetch()}>
            {t('common.retry')}
          </button>
        </StateBox>
      ) : !hosts?.length ? (
        <StateBox icon="○" title={t('remote.no_hosts')} body={t('remote.no_hosts_body')} testId="km-remote-no-hosts">
          <Link className="btn" to="/kmgr/hosts">
            {t('nav.hosts')}
          </Link>
        </StateBox>
      ) : forbidden ? (
        <StateBox icon="⊘" title={t('remote.forbidden')} body={t('remote.forbidden_body')} testId="km-remote-forbidden" />
      ) : tab === 'requests' ? (
        <Requests
          list={assists.list}
          hosts={hosts}
          failed={assists.failed}
          onRetry={assists.refetch}
          onStart={(a) =>
            setOpen({
              target: targetOf(
                hosts.find((h) => h.target_ref === a.device_ref),
                a,
                apiOf(a.home_id),
              ),
              assist: a,
            })
          }
        />
      ) : (
        <Records
          hosts={hosts}
          me={me}
          onResume={(s) =>
            setOpen({
              target: targetOf(
                hosts.find((h) => h.target_ref === s.device_ref),
                s,
                apiOf(s.home_id),
              ),
              resume: s.session_id,
            })
          }
        />
      )}
      {open && <RemoteDialog target={open.target} assist={open.assist} resumeSessionId={open.resume} onClose={() => setOpen(null)} />}
    </section>
  );
}

function Requests({ list, hosts, failed, onRetry, onStart }: { list: AssistRequest[]; hosts: HostRow[]; failed: number; onRetry(): void; onStart(a: AssistRequest): void }) {
  const { t } = useTranslation(NS);
  const { orgs } = useScope();
  const crossOrg = new Set(hosts.map((h) => h.org_id)).size > 1 || orgs.filter((o) => o.kind === 'team').length > 1;
  const now = Date.now();
  const phone = isPhone();
  return (
    <>
      {failed > 0 && (
        <div className="km-bar unk" role="status">
          <span className="ic" aria-hidden>
            ⊘
          </span>
          <span className="txt">{t('remote.assist_partial', { n: failed })}</span>
          <button className="btn btn-sm" onClick={onRetry}>
            {t('common.retry')}
          </button>
        </div>
      )}
      {list.length ? (
        <div className="card">
          <ul className="km-assist" data-testid="km-assist-list">
            {list.map((a) => {
              const h = hosts.find((x) => x.target_ref === a.device_ref);
              const who = h?.primary_user || h?.hostname || t('target.host');
              const where = [crossOrg ? h?.org_name : '', h?.home_name].filter(Boolean).join(' › ');
              const left = Math.max(0, Math.ceil((Date.parse(a.expires_at) - now) / 60_000));
              return (
                <li key={a.request_id} data-request={a.request_id}>
                  <div>
                    <div className="km-assist-title">
                      {t('remote.assist_title', { who })} · <span className="km-assist-code">{t('remote.assist_code', { code: fmtCode(a.code) })}</span>
                    </div>
                    {a.note && <p className="km-assist-note">「{a.note}」</p>}
                    <div className="muted km-sub">
                      {[h?.primary_user && h.hostname ? h.hostname : '', where, t('remote.assist_age', { time: stamp(a.created_at) }), t('remote.assist_left', { n: left })].filter(Boolean).join(' · ')}
                      {a.taken_by_name && <span> · {t('remote.assist_taken', { name: a.taken_by_name })}</span>}
                    </div>
                  </div>
                  <div>
                    <button className="btn btn-primary btn-sm" disabled={a.state !== 'open' || phone} onClick={() => onStart(a)} data-testid="km-assist-start">
                      {t('remote.assist_start')}
                    </button>
                    {phone && <div className="muted km-sub">{t('remote.block.phone')}</div>}
                  </div>
                </li>
              );
            })}
          </ul>
          <p className="muted km-sub" style={{ marginBottom: 0 }}>
            {t('remote.assist_note')}
          </p>
        </div>
      ) : (
        <StateBox icon="○" title={t('remote.assist_none')} body={t('remote.assist_none_body')} testId="km-assist-empty" />
      )}
    </>
  );
}

function Records({ hosts, me, onResume }: { hosts: HostRow[]; me: string; onResume(s: RemoteSession): void }) {
  const { t } = useTranslation(NS);
  const { planes, scope, orgs } = useScope();
  const api = planes[0]?.api ?? '';
  const ks = kmgrScopeOf(scope);
  const [range, setRange] = useState(defaultRange);
  const iso = rangeIso(range.from, range.to);
  const nameOf = useMemberNames(orgs);
  const q = useQuery({
    queryKey: ['kmgr', 'remote', 'records', api, ks.scope, iso.from, iso.to, hosts.map((h) => h.target_ref).join(',')],
    queryFn: () => loadRecords(api, { scope: ks.scope, from: iso.from, to: iso.to }, hosts),
    enabled: !!api && range.from <= range.to,
    staleTime: 10_000,
  });
  const crossOrg = new Set(hosts.map((h) => h.org_id)).size > 1 || orgs.filter((o) => o.kind === 'team').length > 1;
  const operator = (s: RemoteSession) => (s.operator === me ? t('person.you') : s.operator_name || nameOf(s.operator) || t('remote.other_member'));
  const hostLabel = (s: RemoteSession) => s.host_name || t('target.host');
  const homeLabel = (s: RemoteSession) => [crossOrg ? s.org_name : '', s.home_name].filter(Boolean).join(' › ');
  const rows = q.data?.sessions ?? [];
  const exportCsv = () => {
    const out: RecordRow[] = rows.map((s) => {
      const sec = durationSeconds(s);
      return {
        start: stamp(s.created_at),
        end: s.ended_at ? stamp(s.ended_at) : '',
        minutes: s.active_at ? Math.round(sec / 60) : '',
        operator: operator(s),
        org: s.org_name ?? '',
        home: s.home_name ?? '',
        host: hostLabel(s),
        permissions: permsText(t, s.permissions),
        result: resultText(t, s),
        reason: s.reason ?? '',
        note: s.note ?? '',
        external_ticket: s.external_ticket ?? '',
      };
    });
    const header = ['start', 'end', 'minutes', 'operator', 'org', 'home', 'host', 'permissions', 'result', 'reason', 'note', 'external_ticket'].map((k) => t(`remote.csv.${k}`));
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([recordsCsv(out, header)], { type: 'text/csv;charset=utf-8' }));
    a.download = `remote-assist-${range.from}_${range.to}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 60_000);
  };
  return (
    <>
      <div className="km-range" data-testid="km-remote-range">
        <label>
          {t('remote.range_from')}
          <input className="input" type="date" value={range.from} max={range.to} onChange={(e) => setRange({ ...range, from: e.target.value })} />
        </label>
        <label>
          {t('remote.range_to')}
          <input className="input" type="date" value={range.to} min={range.from} onChange={(e) => setRange({ ...range, to: e.target.value })} />
        </label>
        <span className="spacer" />
        <button className="btn" disabled={!rows.length} onClick={exportCsv} data-testid="km-remote-csv">
          {t('remote.export_csv')}
        </button>
      </div>
      {q.isLoading ? (
        <div className="card">
          <SkeletonLines n={4} label={t('common.loading')} />
        </div>
      ) : q.isError ? (
        <StateBox icon="⊘" title={t('remote.records_unreadable')} body={errText(t, q.error)} testId="km-remote-records-error">
          <button className="btn" onClick={() => void q.refetch()}>
            {t('common.retry')}
          </button>
        </StateBox>
      ) : (
        <>
          {q.data && q.data.access !== 'full' && (
            <div className="km-bar unk" role="status" data-testid="km-remote-records-partial" data-access={q.data.access}>
              <span className="ic" aria-hidden>
                ⊘
              </span>
              <span className="txt">{t(`remote.records_access_${q.data.access}`)}</span>
            </div>
          )}
          {rows.length ? (
            <div className="card">
              <table className="km-table km-remote-table" data-testid="km-remote-records">
                <thead>
                  <tr>
                    <th>{t('remote.col_start')}</th>
                    <th>{t('remote.col_host')}</th>
                    <th>{t('remote.col_operator')}</th>
                    <th>{t('remote.col_duration')}</th>
                    <th>{t('remote.col_perms')}</th>
                    <th>{t('remote.col_result')}</th>
                    <th>{t('remote.col_note')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((s) => (
                    <tr key={s.session_id} data-session={s.session_id} data-state={s.state}>
                      <td className="tabular" data-label={t('remote.col_start')}>
                        {stamp(s.created_at)}
                      </td>
                      <td data-label={t('remote.col_host')}>
                        <div>
                          <strong>{hostLabel(s)}</strong>
                          {homeLabel(s) && <div className="muted km-sub">{homeLabel(s)}</div>}
                        </div>
                      </td>
                      <td data-label={t('remote.col_operator')}>{operator(s)}</td>
                      <td className="tabular" data-label={t('remote.col_duration')}>
                        {s.active_at ? durationText(t, durationSeconds(s)) : '—'}
                      </td>
                      <td data-label={t('remote.col_perms')}>{permsText(t, s.permissions)}</td>
                      <td data-label={t('remote.col_result')}>
                        <div>
                          {resultText(t, s)}
                          {s.state !== 'ended' && s.operator === me && (
                            <>
                              {' '}
                              <button className="btn btn-sm" onClick={() => onResume(s)} data-testid="km-remote-resume">
                                {t('remote.resume')}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                      <td data-label={t('remote.col_note')}>
                        <div>
                          {s.note || '—'}
                          {s.external_ticket && (
                            <div className="muted km-sub">
                              {t('remote.ticket_short', {
                                ticket: s.external_ticket,
                              })}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="muted km-sub" style={{ marginBottom: 0 }}>
                {t('remote.records_note')}
              </p>
            </div>
          ) : (
            <StateBox icon="○" title={t('remote.records_none')} body={t('remote.records_none_body')} testId="km-remote-records-empty" />
          )}
        </>
      )}
    </>
  );
}
